import { fetchConfig, debounce } from '@theme/utilities';
import { CartUpdateEvent, RebuyCartChangeEvent } from '@theme/events';

/**
 * Puts the final sale note onto cart lines that the add-to-cart forms could not reach.
 *
 * `snippets/final-sale-property-input.liquid` covers theme-driven add-to-cart, but Rebuy
 * upsells, Giftnote and any other app adding through its own API bypass the form, and carts
 * that already existed before this shipped carry no property. This pass finds those lines
 * and patches them.
 *
 * Loaded globally from `layout/theme.liquid` via `snippets/final-sale-cart-note.liquid`,
 * because the theme only renders a cart at /cart and shoppers can go from the Rebuy flyout
 * straight to checkout.
 */

/**
 * `snippets/scripts.liquid` declares `Theme` with `const` in a classic script, so it lives in
 * the global lexical environment and is NOT a property of `globalThis` — reaching for
 * `globalThis.Theme` yields undefined and silently disables this whole module. Read the
 * binding by name, guarded by `typeof` so a missing `Theme` still cannot throw.
 */
const THEME = typeof Theme === 'undefined' ? undefined : Theme;

const NOTE_KEY = THEME?.translations?.final_sale_note_key;
const NOTE_VALUE = THEME?.translations?.final_sale_note_value;

/**
 * Absolute backstop on iterations, well above any real cart. The working budget is sized to
 * the cart on the first read — a fixed ceiling would silently stop labelling lines partway
 * through a large cart, and most of this catalogue is final sale.
 */
const HARD_PASS_CEILING = 60;

/**
 * `assets/header-actions.js` also listens for `rebuy:cart.change` and runs its athlete pass
 * check on a 1000ms timeout, capturing line keys off Rebuy's cart as it goes. Patching a
 * line mints a new key for it, so this pass has to settle after theirs.
 */
const REBUY_SETTLE_MS = 1500;

/** Storefront root, so locale/market subpaths keep working. Always ends in a slash. */
const ROOT = window.Shopify?.routes?.root ?? '/';

/**
 * Product handle -> carries the `final_sale` tag. Seeded from Liquid with every handle in
 * the cart at page load, so the common case needs no product lookups.
 * @type {Map<string, boolean>}
 */
const finalSaleByHandle = new Map();

/**
 * Line keys not worth trying again — bundle children cannot have their properties changed,
 * for instance. Never retried, so one bad line cannot spin the loop.
 * @type {Set<string>}
 */
const failedKeys = new Set();

/**
 * Line keys already sent to /cart/change on this page. A patch that really landed mints a
 * new key for the line, so seeing the same key twice means the property did not stick and
 * retrying it is pointless.
 * @type {Set<string>}
 */
const attemptedKeys = new Set();

let running = false;

/** Set when a trigger fires while a pass is in flight, so the pass runs again afterwards. */
let rerunRequested = false;

/**
 * Reads the state Liquid rendered into the page.
 * @returns {{needsReconcile?: boolean, finalSaleByHandle?: Record<string, boolean>}}
 */
function readState() {
  const element = document.getElementById('final-sale-cart-state');
  if (!element?.textContent) return {};

  try {
    return JSON.parse(element.textContent);
  } catch {
    return {};
  }
}

/**
 * Whether a product carries the `final_sale` tag. /cart.js does not expose product tags, so
 * anything not seeded from Liquid is looked up once and cached.
 * @param {string | undefined} handle - The product handle.
 * @returns {Promise<boolean>}
 */
async function isFinalSale(handle) {
  if (!handle) return false;

  const cached = finalSaleByHandle.get(handle);
  if (cached !== undefined) return cached;

  let result = false;

  try {
    const response = await fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });

    // A 404 means the product is not published to the online store. Treat it as not final
    // sale rather than retrying it on every cart change.
    if (response.ok) {
      const product = await response.json();
      result = Array.isArray(product.tags) && product.tags.includes('final_sale');
    }
  } catch {
    // Network failure. Cache the miss for this page view; the next page load re-seeds from
    // Liquid anyway.
  }

  finalSaleByHandle.set(handle, result);
  return result;
}

/**
 * @returns {Promise<any>} The current cart, or null if it could not be read.
 */
async function getCart() {
  try {
    const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * The cart sections rendered on this page, so a patch can come back with fresh HTML for
 * them. Empty everywhere except /cart.
 * @returns {string[]}
 */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter((sectionId) => Boolean(sectionId));
}

/**
 * Finds the first line that should carry the note but does not.
 * @param {any[]} items - The cart line items.
 * @returns {Promise<any>} The line, or null if every line is already correct.
 */
async function findLineNeedingNote(items) {
  for (const item of items) {
    if (failedKeys.has(item.key)) continue;
    if (item.properties?.[NOTE_KEY] === NOTE_VALUE) continue;

    // `assets/header-actions.js` manages this line by key on its own timer. Changing its
    // properties would invalidate the key it captured, so leave it alone.
    if (item.properties?._is_athlete_pass) continue;

    if (!(await isFinalSale(item.handle))) continue;

    return item;
  }

  return null;
}

/**
 * Adds the note to one line.
 * @param {any} line - The cart line item.
 * @returns {Promise<any>} The updated cart, or null if the patch failed.
 */
async function patchLine(line) {
  const sectionIds = cartSectionIds();

  const body = JSON.stringify({
    id: line.key,
    quantity: line.quantity,
    // /cart/change replaces the whole properties object for a line, so merge rather than
    // overwrite: Giftnote stores gift messages here, and app-set `_`-prefixed properties
    // have to survive.
    properties: { ...line.properties, [NOTE_KEY]: NOTE_VALUE },
    ...(sectionIds.length > 0 && {
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  try {
    const response = await fetch(THEME.routes.cart_change_url, fetchConfig('json', { body }));
    if (!response.ok) return null;

    const cart = await response.json();
    if (cart.errors) return null;

    return cart;
  } catch {
    return null;
  }
}

/**
 * Lets the rest of the theme know the cart changed. `assets/component-cart-items.js` already
 * listens for this and morphs from `detail.data.sections`, so /cart re-renders itself.
 * @param {any} cart - The updated cart.
 */
function announce(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'final-sale-note', {
      source: 'final-sale-note',
      itemCount: cart.item_count,
      sections: cart.sections,
    })
  );
}

/**
 * Asks Rebuy to re-read the cart, so a flyout that is already open picks the note up rather
 * than waiting to be reopened.
 *
 * `Rebuy.Cart.getCart()` is the call to use: checked against the live storefront, where
 * `SmartCart.updateCart`, `SmartCart.refresh` and `SmartCart.getCart` do not exist at all.
 * Letting Rebuy fetch for itself keeps its own cart enrichment intact, which handing it a
 * raw Shopify cart through `setCart` would not.
 *
 * Rebuy is a third-party app and its API can change without notice, so the call stays
 * guarded. If it disappears the note still shows the next time the flyout opens and is
 * already correct on /cart and in checkout — this must degrade, never throw.
 */
function nudgeRebuy() {
  try {
    const getCart = window.Rebuy?.Cart?.getCart;
    if (typeof getCart === 'function') getCart.call(window.Rebuy.Cart);
  } catch {
    // Nothing to do — see above.
  }
}

async function reconcile() {
  if (running) {
    // A cart change landed mid-pass. Remember it instead of dropping it, or the line it added
    // waits for the next page load — which may never come before checkout.
    rerunRequested = true;
    return;
  }

  running = true;

  /** The last cart a patch returned, so the UI is refreshed once rather than per line. */
  let patchedCart = null;

  try {
    let budget = HARD_PASS_CEILING;

    for (let pass = 0; pass < budget; pass++) {
      // Re-read the cart every pass: patching a line mints a new key for it, so a list
      // captured up front goes stale immediately.
      const cart = await getCart();
      if (!cart?.items?.length) break;

      // One pass per line, plus headroom for a retry and the final confirming read.
      if (pass === 0) budget = Math.min(cart.items.length * 2 + 2, HARD_PASS_CEILING);

      const line = await findLineNeedingNote(cart.items);
      if (!line) break;

      // Same key on a second sighting means the last patch reported success but the property
      // did not stick. Stop rather than spending the remaining passes on it.
      if (attemptedKeys.has(line.key)) {
        failedKeys.add(line.key);
        continue;
      }
      attemptedKeys.add(line.key);

      const updated = await patchLine(line);
      if (!updated) {
        failedKeys.add(line.key);
        continue;
      }

      patchedCart = updated;
    }
  } catch (error) {
    console.error('Final sale note reconciliation failed', error);
  } finally {
    running = false;
  }

  if (patchedCart) {
    announce(patchedCart);
    nudgeRebuy();
  }

  if (rerunRequested) {
    rerunRequested = false;
    await reconcile();
  }
}

if (NOTE_KEY && NOTE_VALUE) {
  const state = readState();

  for (const [handle, finalSale] of Object.entries(state.finalSaleByHandle ?? {})) {
    finalSaleByHandle.set(handle, finalSale === true);
  }

  if (state.needsReconcile) reconcile();

  // Covers lines added by Rebuy upsells and anything else that changes the cart behind the
  // theme's back. Theme add-to-cart already carries the property via the hidden input, and
  // this deliberately does not listen for the theme's own cart:update, so a patch cannot
  // re-trigger the pass.
  document.addEventListener(RebuyCartChangeEvent.eventName, debounce(reconcile, REBUY_SETTLE_MS));
}
